// ============================================================
// Prompt generators pour analyse AI via Claude (scenario 2 - bridge manuel)
// Génère des prompts riches en contexte à coller dans Claude Pro
// ============================================================

import { getDeathsAnalytics, getMatchupAnalytics, getTrendAnalytics } from './analytics';

const BLOCK_OBJECTIVES = `
# Objectifs du bloc de 100 games (Road to Challenger ADC)

## Axes de travail
1. **Morts** : objectif < 4 morts/game, 0 mort greed idéalement
2. **Macro individuelle** : où est l'or safe, ma win condition, ce qui me tue si je fais rien
3. **Mental** : max 3 ranked/session, 2 loses = stop, zéro role swap (100 games)
4. **Review** : systématique sur les défaites ressenties

## Cibles du bloc
- Winrate > 54%
- Morts moyennes < 4/game
- % games sans mort greed > 70%
- Pool resserré : Caitlyn, Kai'Sa, Lucian (mains), Corki, Jhin (flex)
`;

export function buildGameAnalysisPrompt(game, allGames, sessions, miniBlocks, intent) {
  const recent = allGames.slice(-10);
  const deathsAnalytics = getDeathsAnalytics(allGames);
  const matchupData = getMatchupAnalytics(allGames);
  const trends = getTrendAnalytics(allGames);

  const gameNumber = game.gameNumber || allGames.length;
  const prev5 = allGames.slice(-6, -1);

  let prompt = `Tu es Apex, mon coach personnel ADC pour mon climb vers Challenger sur LoL EUW (IGN: Nabousan). Mon niveau actuel est Master (peak GM saison dernière).

${BLOCK_OBJECTIVES}

---

# GAME À ANALYSER (Game #${gameNumber})

**Résultat** : ${game.result === 'W' ? 'VICTOIRE' : 'DÉFAITE'} · Durée : ${game.duration}min

## Composition
- **Moi** : ${game.champion} + ${game.support} (support allié)
- **Botlane ennemie** : ${game.enemyAdc} + ${game.enemySupport}
- **Jungler allié** : ${game.jungler} · **Jungler ennemi** : ${game.enemyJungler}

## Stats
- **KDA** : ${game.kills}/${game.deaths}/${game.assists} (${game.deaths === 0 ? 'PERFECT' : ((game.kills + game.assists) / game.deaths).toFixed(2)})
- **CS/min** : ${game.csPerMin}
${game.damageDealt > 0 ? `- **Damage perso** : ${game.damageDealt.toLocaleString()} (${Math.round(game.damageDealt / game.duration).toLocaleString()}/min)` : ''}
${game.teamDamage > 0 ? `- **Damage share** : ${(game.damageDealt / game.teamDamage * 100).toFixed(1)}%` : ''}
- **Feeling** : ${game.feeling}/10 · **Mental** : ${game.mental}

## Morts catégorisées (${game.deathDetails?.length || 0})
${game.deathDetails?.length > 0 ? game.deathDetails.map(d => `- ${d.minute}min [${d.type.toUpperCase()}] : ${d.context}`).join('\n') : 'Aucune mort catégorisée'}

${game.keyMoments ? `## Notes de la game\n"${game.keyMoments}"\n` : ''}

${intent ? `---

# INTENT AVANT LA GAME (à matcher avec la réalité)
- **Mental pré-queue** : ${intent.mental_pre_queue}/10
- **Focus visé** : ${intent.focus_area || 'N/A'}
- **Pick envisagé** : ${intent.intended_pick || 'N/A'}
${intent.notes ? `- **Notes** : ${intent.notes}` : ''}

*Dis-moi si j'ai respecté mon intent, et si non pourquoi.*
` : ''}

---

# CONTEXTE DU BLOC

## Progression globale (${allGames.length} games jouées)
- **Winrate** : ${trends.trends[trends.trends.length - 1]?.winrate || 0}%
- **Morts/game (rolling 10)** : ${trends.trends[trends.trends.length - 1]?.avgDeaths || 0}
- **% games clean (rolling 10)** : ${trends.trends[trends.trends.length - 1]?.cleanRatio || 0}%
- **Streak actuelle** : ${trends.currentStreak.count}${trends.currentStreak.type}

## Mes 5 dernières games (avant celle-ci)
${prev5.length > 0 ? prev5.map((g, i) => `${i + 1}. ${g.result} · ${g.champion} ${g.kills}/${g.deaths}/${g.assists} vs ${g.enemyAdc}+${g.enemySupport} (${g.duration}min)`).join('\n') : 'Pas de games précédentes'}

## Distribution de morts globale (${deathsAnalytics.total} morts cumulées)
- Investment : ${deathsAnalytics.byType.investment} (${Math.round(deathsAnalytics.byType.investment / deathsAnalytics.total * 100) || 0}%)
- Position : ${deathsAnalytics.byType.position} (${Math.round(deathsAnalytics.byType.position / deathsAnalytics.total * 100) || 0}%)
- Greed : ${deathsAnalytics.byType.greed} (${Math.round(deathsAnalytics.byType.greed / deathsAnalytics.total * 100) || 0}%)

${miniBlocks.filter(b => b.status === 'active').length > 0 ? `## Mini-blocs objectifs actifs
${miniBlocks.filter(b => b.status === 'active').map(b => `- ${b.title} (${b.description || ''})`).join('\n')}
` : ''}

---

# MA DEMANDE

Analyse cette game et donne-moi :

1. **Diagnostic** (2-3 phrases max) : qu'est-ce qui a fait gagner ou perdre cette game ? Focus sur MON impact, pas sur l'équipe.

2. **Pattern identifié** (s'il y en a un) : est-ce que tu vois un pattern récurrent en comparant avec mes 5-10 dernières games ? Sois concret et direct, cite-moi les games comparables si pertinent.

3. **2-3 actions pour ma prochaine game** : actions concrètes, mesurables, directement applicables. Pas de généralités. Exemple de bonne action : "Quand tu es dans un matchup Caitlyn+Lulu vs Kai'Sa+Nautilus, prends Doran's Shield au lieu de Doran's Blade et priorise le level 2 avant eux".

${intent ? '4. **Respect de l\'intent** : est-ce que j\'ai respecté mon focus pré-queue ? Si non, pourquoi et comment corriger ?\n' : ''}

Sois direct, pas de flatteries. Je préfère une analyse crue qui me pousse à progresser qu'un diagnostic gentil. Format court, lisible, actionnable.`;

  return prompt;
}

export function buildSessionAnalysisPrompt(session, sessionGames, allGames, miniBlocks) {
  const wins = sessionGames.filter(g => g.result === 'W').length;
  const losses = sessionGames.length - wins;
  const totalDeaths = sessionGames.reduce((a, g) => a + g.deaths, 0);
  const greedDeaths = sessionGames.reduce((a, g) => a + (g.deathDetails || []).filter(d => d.type === 'greed').length, 0);

  let prompt = `Tu es Apex, mon coach personnel ADC pour mon climb vers Challenger sur LoL EUW (IGN: Nabousan). Mon niveau actuel est Master (peak GM saison dernière).

${BLOCK_OBJECTIVES}

---

# SESSION À ANALYSER

**Date** : ${new Date(session.closed_at).toLocaleString('fr-FR')}
**Résultat** : ${wins}W / ${losses}L sur ${sessionGames.length} games
**Winrate session** : ${Math.round(wins / sessionGames.length * 100)}%
**Mental global** : ${session.mental_global || 'N/A'}
${session.insights ? `**Mes insights** : "${session.insights}"` : ''}

## Détail des games

${sessionGames.map((g, i) => `
### Game ${i + 1} — ${g.result} · ${g.champion} (${g.kills}/${g.deaths}/${g.assists}) · ${g.duration}min
- vs ${g.enemyAdc}+${g.enemySupport} · allié: ${g.support} · jg: ${g.jungler} vs ${g.enemyJungler}
- CS/min: ${g.csPerMin}${g.damageDealt > 0 ? ` · DMG/min: ${Math.round(g.damageDealt / g.duration).toLocaleString()}` : ''}${g.teamDamage > 0 ? ` · DMG%: ${(g.damageDealt / g.teamDamage * 100).toFixed(1)}%` : ''}
- Feeling: ${g.feeling}/10 · Mental: ${g.mental}
${g.deathDetails?.length > 0 ? `- Morts: ${g.deathDetails.map(d => `${d.minute}min [${d.type}] ${d.context}`).join(' | ')}` : '- Aucune mort catégorisée'}
${g.keyMoments ? `- Notes: "${g.keyMoments}"` : ''}
`).join('')}

## Stats agrégées session
- Morts totales : ${totalDeaths} (${(totalDeaths / sessionGames.length).toFixed(1)}/game)
- Morts greed : ${greedDeaths}
- Games clean (sans greed) : ${sessionGames.filter(g => !(g.deathDetails || []).some(d => d.type === 'greed')).length}/${sessionGames.length}

---

# CONTEXTE GLOBAL
- Games totales dans le bloc : ${allGames.length}/100
${miniBlocks.filter(b => b.status === 'active').length > 0 ? `- Objectifs actifs : ${miniBlocks.filter(b => b.status === 'active').map(b => b.title).join(', ')}` : ''}

---

# MA DEMANDE

Fais une review de session structurée :

1. **Thème de la session** (1 phrase) : qu'est-ce qui a caractérisé cette session ? (bonne mécanique, mauvais mental, matchups difficiles, etc.)

2. **Ce qui a marché** (2-3 points) : identifie concrètement ce qui a fait tes victoires ou les moments forts de chaque game.

3. **Ce qui a bloqué** (2-3 points) : identifie les vraies raisons des défaites ou des moments faibles. Sois honnête, pas d'excuses type "adc jungler diff".

4. **Pattern cross-games** : est-ce qu'il y a un fil rouge qui traverse plusieurs games ? (ex : "tu meurs systématiquement à 20-22min", "tu perds toutes tes games Lucian vs un Ezreal")

5. **Focus pour la prochaine session** : 1 SEUL objectif précis et mesurable pour la session suivante.

6. **Mini-bloc suggéré** (optionnel) : si tu vois un problème net et récurrent, propose-moi un mini-bloc de 10 games avec objectif mesurable pour le résoudre.

Sois direct et spécifique. Format structuré et lisible. Pas de généralités coaching bullshit, du concret actionnable.`;

  return prompt;
}
